import {
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";

import {
  CheckCircle2,
  CircleDollarSign,
  LoaderCircle,
  MessageCircle,
  MessagesSquare,
  ExternalLink,
  X,
  PackageCheck,
  Search,
  ShoppingBag
} from "lucide-react";

import {
  useState
} from "react";

import {
  getAdminOrders,
  getAdminOrderWhatsAppMessage,
  updateAdminOrder
} from "../services/admin";

import {
  AdminOrderDetailsModal
} from "../components/admin/orders/AdminOrderDetailsModal";

import type {
  AdminOrder,
  AdminOrderStatus,
  AdminPaymentStatus,
  AdminWhatsAppEvent,
  AdminWhatsAppMessage
} from "../types/admin";

const orderStatuses: AdminOrderStatus[] = [
  "NEW",
  "CONFIRMED",
  "PACKING",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED"
];

const paymentStatuses: AdminPaymentStatus[] = [
  "PENDING",
  "PAID"
];

const communicationActions: {
  event: AdminWhatsAppEvent;
  label: string;
}[] = [
  {
    event: "ORDER_RECEIVED",
    label: "Order Received"
  },
  {
    event: "PAYMENT_PENDING",
    label: "Payment Reminder"
  },
  {
    event: "ORDER_CONFIRMED",
    label: "Confirmed"
  },
  {
    event: "PACKING_STARTED",
    label: "Packing"
  },
  {
    event: "ORDER_SHIPPED",
    label: "Shipped"
  },
  {
    event: "ORDER_DELIVERED",
    label: "Delivered"
  }
];

function readableStatus(
  value: string
): string {
  return value
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(
      /\b\w/g,
      (character) =>
        character.toUpperCase()
    );
}

function formatAmount(
  value: string
): string {
  return Number(
    value
  ).toLocaleString(
    "en-IN"
  );
}

function buildAdminWhatsAppMessage(
  order: AdminOrder
): string {
  const products = order.items
    .map(
      (item) =>
        `• ${item.productName} x${item.quantity}`
    )
    .join("\n");

  return [
    `Hello ${order.customerName},`,
    "",
    `Regarding your RiNi Bodycare order ${order.orderNumber}.`,
    "",
    products,
    "",
    `Product Subtotal: ₹${formatAmount(order.subtotal)}`,
    `Shipping: ₹${formatAmount(order.shippingAmount)}`,
    `Final Payable Amount: ₹${formatAmount(order.totalAmount)}`,
    "",
    "Please complete the payment and share the confirmation.",
    "",
    "Thank you for choosing RiNi Bodycare."
  ].join("\n");
}

function OrderCard({
  order
}: {
  order: AdminOrder;
}) {
  const queryClient =
    useQueryClient();

  const [
    shippingInput,
    setShippingInput
  ] =
    useState(
      String(
        Number(
          order.shippingAmount
        )
      )
    );

  const [
    showDetails,
    setShowDetails
  ] =
    useState(false);

  const [
    communicationEvent,
    setCommunicationEvent
  ] =
    useState<
      AdminWhatsAppEvent |
      null
    >(null);

  const [
    communicationPreview,
    setCommunicationPreview
  ] =
    useState<
      AdminWhatsAppMessage |
      null
    >(null);

  const updateMutation =
    useMutation({
      mutationFn: (
        input: {
          orderStatus?: AdminOrderStatus;
          paymentStatus?: AdminPaymentStatus;
          shippingAmount?: number;
        }
      ) =>
        updateAdminOrder(
          order.id,
          input
        ),

      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: [
            "admin",
            "orders"
          ]
        });
      }
    });

  const messageMutation =
    useMutation({
      mutationFn: (
        event:
          AdminWhatsAppEvent
      ) =>
        getAdminOrderWhatsAppMessage(
          order.id,
          event
        ),

      onSuccess: (
        data
      ) => {
        setCommunicationPreview(
          data
        );
      }
    });

  const whatsappNumber = (
    order.whatsappNumber ||
    order.phone
  ).replace(
    /\D/g,
    ""
  );

  const whatsappMessage =
    buildAdminWhatsAppMessage(
      order
    );

  const whatsappUrl =
    `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
      whatsappMessage
    )}`;

  return (
    <article className="rounded-[2rem] border border-[#5e3b2c]/10 bg-white p-6 shadow-sm">
      <div className="flex flex-col gap-5 border-b border-[#5e3b2c]/10 pb-6 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-serif text-2xl text-[#38251e]">
              {order.orderNumber}
            </h2>

            <span className="rounded-full bg-[#edf1e8] px-3 py-1 text-xs font-semibold text-[#526746]">
              {readableStatus(
                order.orderStatus
              )}
            </span>

            <span className={[
              "rounded-full px-3 py-1 text-xs font-semibold",
              order.paymentStatus === "PAID"
                ? "bg-green-50 text-green-700"
                : "bg-amber-50 text-amber-700"
            ].join(" ")}>
              Payment:
              {" "}
              {readableStatus(
                order.paymentStatus
              )}
            </span>
          </div>

          <p className="mt-3 text-sm text-[#746d65]">
            {new Date(
              order.createdAt
            ).toLocaleString(
              "en-IN"
            )}
          </p>
        </div>

        <div className="text-left lg:text-right">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70845b]">
            Order total
          </p>

          <p className="mt-1 text-3xl font-semibold text-[#5e3b2c]">
            ₹
            {formatAmount(
              order.totalAmount
            )}
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-8 lg:grid-cols-3">
        <section>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70845b]">
            Customer
          </p>

          <p className="mt-3 font-semibold text-[#38251e]">
            {order.customerName}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            {order.phone}
          </p>

          {order.email && (
            <p className="mt-1 break-all text-sm text-[#746d65]">
              {order.email}
            </p>
          )}

          <a
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#edf1e8] px-4 py-2.5 text-sm font-semibold text-[#526746]"
          >
            <MessageCircle
              size={16}
            />

            WhatsApp customer
          </a>
        </section>

        <section>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70845b]">
            Delivery address
          </p>

          <p className="mt-3 text-sm leading-6 text-[#746d65]">
            {order.addressLine1}
            <br />

            {order.addressLine2 && (
              <>
                {order.addressLine2}
                <br />
              </>
            )}

            {order.city},
            {" "}
            {order.state}
            <br />

            PIN:
            {" "}
            {order.postalCode}
          </p>
        </section>

        <section>
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#70845b]">
            Products
          </p>

          <div className="mt-3 space-y-3">
            {order.items.map(
              (item) => (
                <div
                  key={item.id}
                  className="flex justify-between gap-4 text-sm"
                >
                  <div>
                    <p className="font-medium text-[#38251e]">
                      {item.productName}
                    </p>

                    <p className="text-xs text-[#746d65]">
                      ₹
                      {formatAmount(
                        item.unitPrice
                      )}
                      {" "}
                      ×
                      {" "}
                      {item.quantity}
                    </p>
                  </div>

                  <p className="font-semibold text-[#38251e]">
                    ₹
                    {formatAmount(
                      item.lineTotal
                    )}
                  </p>
                </div>
              )
            )}
          </div>
        </section>
      </div>

      <div className="mt-6 grid gap-4 rounded-2xl bg-[#fffaf4] p-5 sm:grid-cols-3">
        <div>
          <p className="text-xs text-[#746d65]">
            Subtotal
          </p>

          <p className="mt-1 font-semibold text-[#38251e]">
            ₹
            {formatAmount(
              order.subtotal
            )}
          </p>
        </div>

        <div>
          <p className="text-xs text-[#746d65]">
            Shipping
          </p>

          <div className="mt-2 flex items-center gap-2">
            <span className="text-sm font-semibold text-[#38251e]">
              ₹
            </span>

            <input
              type="number"
              min="0"
              step="1"
              value={shippingInput}
              onChange={(event) =>
                setShippingInput(
                  event.target.value
                )
              }
              className="w-24 rounded-xl border border-[#5e3b2c]/15 bg-white px-3 py-2 text-sm"
            />

            <button
              type="button"
              disabled={
                updateMutation.isPending
              }
              onClick={() => {
                const value =
                  Number(
                    shippingInput
                  );

                if (
                  !Number.isFinite(
                    value
                  ) ||
                  value < 0
                ) {
                  return;
                }

                updateMutation.mutate({
                  shippingAmount:
                    value
                });
              }}
              className="rounded-full bg-[#5e3b2c] px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>

        <div>
          <p className="text-xs text-[#746d65]">
            Payment method
          </p>

          <p className="mt-1 font-semibold text-[#38251e]">
            Manual
          </p>
        </div>
      </div>

      <section className="mt-6 rounded-2xl border border-[#5e3b2c]/10 bg-[#f8f4ee] p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <MessagesSquare
                size={19}
                className="text-[#70845b]"
              />

              <p className="font-serif text-xl text-[#38251e]">
                Customer Communication
              </p>
            </div>

            <p className="mt-2 text-sm text-[#746d65]">
              Generate a ready-to-send WhatsApp message for this order.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {communicationActions.map(
              (action) => (
                <button
                  key={action.event}
                  type="button"
                  disabled={
                    messageMutation.isPending
                  }
                  onClick={() => {
                    setCommunicationEvent(
                      action.event
                    );

                    messageMutation.mutate(
                      action.event
                    );
                  }}
                  className={[
                    "rounded-full border px-4 py-2 text-xs font-semibold transition",
                    communicationEvent ===
                      action.event
                      ? "border-[#5e3b2c] bg-[#5e3b2c] text-white"
                      : "border-[#5e3b2c]/15 bg-white text-[#5e3b2c] hover:bg-[#fffaf4]"
                  ].join(" ")}
                >
                  {action.label}
                </button>
              )
            )}
          </div>
        </div>

        {messageMutation.isPending && (
          <div className="mt-5 flex items-center gap-2 rounded-xl bg-white p-4 text-sm text-[#746d65]">
            <LoaderCircle
              size={18}
              className="animate-spin"
            />

            Preparing WhatsApp message...
          </div>
        )}

        {messageMutation.isError && (
          <div className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
            Message could not be generated.
          </div>
        )}

        {communicationPreview && (
          <div className="mt-5 rounded-2xl bg-white p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70845b]">
                  Message Preview
                </p>

                <p className="mt-1 font-semibold text-[#38251e]">
                  {communicationPreview.title}
                </p>

                <p className="mt-1 text-xs text-[#746d65]">
                  To: {communicationPreview.targetNumber}
                </p>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setCommunicationPreview(
                      null
                    );

                    setCommunicationEvent(
                      null
                    );
                  }}
                  className="inline-flex items-center gap-2 rounded-full border border-[#5e3b2c]/15 px-4 py-2 text-xs font-semibold text-[#5e3b2c]"
                >
                  <X size={14} />
                  Close
                </button>

                <a
                  href={
                    communicationPreview.whatsappUrl
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-[#526746] px-4 py-2 text-xs font-semibold text-white"
                >
                  <MessageCircle
                    size={14}
                  />

                  Open WhatsApp

                  <ExternalLink
                    size={13}
                  />
                </a>
              </div>
            </div>

            <div className="mt-4 whitespace-pre-wrap rounded-xl bg-[#edf1e8] p-4 text-sm leading-7 text-[#38251e]">
              {communicationPreview.message}
            </div>
          </div>
        )}
      </section>

      {order.notes && (
        <div className="mt-5 rounded-2xl bg-[#edf1e8] p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#70845b]">
            Customer note
          </p>

          <p className="mt-2 text-sm leading-6 text-[#746d65]">
            {order.notes}
          </p>
        </div>
      )}

      <div className="mt-6">
        <button
          type="button"
          onClick={() =>
            setShowDetails(
              true
            )
          }
          className="rounded-full border border-[#5e3b2c]/15 bg-white px-5 py-2.5 text-sm font-semibold text-[#5e3b2c] transition hover:bg-[#fffaf4]"
        >
          View full order details
        </button>
      </div>

      <div className="mt-6 flex flex-col gap-4 border-t border-[#5e3b2c]/10 pt-6 sm:flex-row sm:flex-wrap sm:items-end">
        <label>
          <span className="mb-2 block text-xs font-semibold text-[#746d65]">
            Order status
          </span>

          <select
            value={order.orderStatus}
            disabled={
              updateMutation.isPending
            }
            onChange={(event) =>
              updateMutation.mutate({
                orderStatus:
                  event.target
                    .value as AdminOrderStatus
              })
            }
            className="min-w-44 rounded-xl border border-[#5e3b2c]/15 bg-white px-4 py-3 text-sm"
          >
            {orderStatuses.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {readableStatus(
                    status
                  )}
                </option>
              )
            )}
          </select>
        </label>

        <label>
          <span className="mb-2 block text-xs font-semibold text-[#746d65]">
            Payment status
          </span>

          <select
            value={
              order.paymentStatus
            }
            disabled={
              updateMutation.isPending
            }
            onChange={(event) =>
              updateMutation.mutate({
                paymentStatus:
                  event.target
                    .value as AdminPaymentStatus
              })
            }
            className="min-w-40 rounded-xl border border-[#5e3b2c]/15 bg-white px-4 py-3 text-sm"
          >
            {paymentStatuses.map(
              (status) => (
                <option
                  key={status}
                  value={status}
                >
                  {readableStatus(
                    status
                  )}
                </option>
              )
            )}
          </select>
        </label>

        {updateMutation.isPending && (
          <span className="flex items-center pb-3 text-sm text-[#746d65]">
            <LoaderCircle
              size={16}
              className="mr-2 animate-spin"
            />

            Saving...
          </span>
        )}

        {updateMutation.isError && (
          <span className="pb-3 text-sm text-red-700">
            Update failed.
          </span>
        )}
      </div>

      {showDetails && (
        <AdminOrderDetailsModal
          order={order}
          onClose={() =>
            setShowDetails(
              false
            )
          }
        />
      )}
    </article>
  );
}

export function AdminOrdersPage() {
  const [search, setSearch] =
    useState("");

  const [orderStatus, setOrderStatus] =
    useState("");

  const [paymentStatus, setPaymentStatus] =
    useState("");

  const ordersQuery =
    useQuery({
      queryKey: [
        "admin",
        "orders",
        search,
        orderStatus,
        paymentStatus
      ],

      queryFn: () =>
        getAdminOrders({
          search:
            search.trim() ||
            undefined,

          orderStatus:
            orderStatus ||
            undefined,

          paymentStatus:
            paymentStatus ||
            undefined,

          page: 1,
          limit: 50
        })
    });

  const orders =
    ordersQuery.data?.orders ??
    [];

  const total =
    ordersQuery.data?.pagination
      .total ??
    0;

  const newCount =
    orders.filter(
      (order) =>
        order.orderStatus ===
        "NEW"
    ).length;

  const pendingPaymentCount =
    orders.filter(
      (order) =>
        order.paymentStatus ===
        "PENDING"
    ).length;

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#70845b]">
          Website orders
        </p>

        <h1 className="mt-2 font-serif text-4xl text-[#38251e]">
          Orders
        </h1>

        <p className="mt-3 max-w-2xl text-[#746d65]">
          Review incoming website orders,
          confirm manual payments and manage
          fulfilment from one place.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl bg-white p-5">
          <ShoppingBag
            size={22}
            className="text-[#70845b]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {total}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Total orders
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5">
          <PackageCheck
            size={22}
            className="text-[#70845b]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {newCount}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            New orders
          </p>
        </div>

        <div className="rounded-2xl bg-white p-5">
          <CircleDollarSign
            size={22}
            className="text-[#a27a4d]"
          />

          <p className="mt-4 text-3xl font-semibold text-[#38251e]">
            {pendingPaymentCount}
          </p>

          <p className="mt-1 text-sm text-[#746d65]">
            Payment pending
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 lg:flex-row">
        <label className="relative flex-1">
          <Search
            size={17}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#746d65]"
          />

          <input
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Search order, customer, phone, product or SKU"
            className="w-full rounded-xl border border-[#5e3b2c]/15 bg-[#fffdf8] py-3 pl-11 pr-4 text-sm"
          />
        </label>

        <select
          value={orderStatus}
          onChange={(event) =>
            setOrderStatus(
              event.target.value
            )
          }
          className="rounded-xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3 text-sm"
        >
          <option value="">
            All order statuses
          </option>

          {orderStatuses.map(
            (status) => (
              <option
                key={status}
                value={status}
              >
                {readableStatus(
                  status
                )}
              </option>
            )
          )}
        </select>

        <select
          value={paymentStatus}
          onChange={(event) =>
            setPaymentStatus(
              event.target.value
            )
          }
          className="rounded-xl border border-[#5e3b2c]/15 bg-[#fffdf8] px-4 py-3 text-sm"
        >
          <option value="">
            All payment statuses
          </option>

          {paymentStatuses.map(
            (status) => (
              <option
                key={status}
                value={status}
              >
                {readableStatus(
                  status
                )}
              </option>
            )
          )}
        </select>
      </div>

      {ordersQuery.isLoading && (
        <div className="flex min-h-80 items-center justify-center">
          <LoaderCircle
            size={36}
            className="animate-spin text-[#70845b]"
          />
        </div>
      )}

      {ordersQuery.isError && (
        <div className="rounded-2xl bg-red-50 p-6 text-red-700">
          Orders could not be loaded.
        </div>
      )}

      {!ordersQuery.isLoading &&
        !ordersQuery.isError &&
        orders.length === 0 && (
          <div className="rounded-[2rem] bg-white p-12 text-center">
            <PackageCheck
              size={40}
              className="mx-auto text-[#70845b]"
            />

            <h2 className="mt-5 font-serif text-2xl text-[#38251e]">
              No matching orders
            </h2>

            <p className="mt-2 text-[#746d65]">
              New website orders will appear here.
            </p>
          </div>
        )}

      <div className="space-y-6">
        {orders.map(
          (order) => (
            <OrderCard
              key={order.id}
              order={order}
            />
          )
        )}
      </div>

      {total > 0 && (
        <div className="flex items-center gap-2 text-sm text-[#526746]">
          <CheckCircle2
            size={17}
          />

          Showing
          {" "}
          {orders.length}
          {" "}
          of
          {" "}
          {total}
          {" "}
          order(s)
        </div>
      )}
    </div>
  );
}
